// @ts-nocheck
/*
 * Port of views/dashboard/call-centre/create.js (origin/main-dev, 1401 lines).
 * BYTE-IDENTICAL LOGIC — every state hook, useEffect, API call, toast,
 * licence math (validateInput/stepLicence/getLicenceMax), demo-creator
 * caps (auto-fill 4 keys, expiry inheritance), country/timezone auto-
 * default for India → Asia/Kolkata, default-user role selection with
 * `mustForceCallCentreAdminDefaultUser`, voicemail ACL gate, and JSX
 * conditions match v1 to the letter.
 *
 * Chrome kept: react-bootstrap Form/Row/Col/Button, react-icons,
 * v1 <Card> wrapper → shadcn <Card>.
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Button } from '@/components/ui/button';

/* Local micro-shims for react-bootstrap Row/Col/Form.* — see
 * CallCentreEditPage.tsx for the identical block. */
const _cx = (...args: any[]) => args.filter(Boolean).join(' ');
const Row: any = ({ className, children, ...props }: any) => (
  <div className={_cx('row', className)} {...props}>
    {children}
  </div>
);
const _colCls = ({ xs, sm, md, lg, xl }: any) => {
  const parts: string[] = [];
  const push = (bp: string, val: any) => {
    if (val === undefined || val === null || val === false) return;
    parts.push(bp === 'xs' ? `col-${val}` : `col-${bp}-${val}`);
  };
  push('xs', xs); push('sm', sm); push('md', md); push('lg', lg); push('xl', xl);
  return parts.length ? parts.join(' ') : 'col';
};
const Col: any = ({ xs, sm, md, lg, xl, className, children, ...props }: any) => (
  <div className={_cx(_colCls({ xs, sm, md, lg, xl }), className)} {...props}>
    {children}
  </div>
);
const Form: any = ({ children, onSubmit, className, ...props }: any) => (
  <form className={className} onSubmit={onSubmit || ((e: any) => e.preventDefault())} {...props}>
    {children}
  </form>
);
Form.Group = ({ className, controlId: _c, children, ...props }: any) => (
  <div className={_cx('mb-3', className)} {...props}>{children}</div>
);
Form.Label = ({ className, children, htmlFor, ...props }: any) => (
  <label className={_cx('form-label', className)} htmlFor={htmlFor} {...props}>{children}</label>
);
Form.Control = ({ as, className, children, isInvalid, ...props }: any) => {
  const cls = _cx('form-control', isInvalid && 'is-invalid', className);
  if (as === 'select') return <select className={cls} {...props}>{children}</select>;
  if (as === 'textarea') return <textarea className={cls} {...props} />;
  return <input className={cls} {...props} />;
};
Form.Select = ({ className, children, ...props }: any) => (
  <select className={_cx('form-control form-select', className)} {...props}>{children}</select>
);
Form.Check = ({ type = 'checkbox', label, id, className, inline, children, ...props }: any) => (
  <div className={_cx('form-check', inline && 'form-check-inline', className)}>
    <input type={type} id={id} className="form-check-input" {...props} />
    {label !== undefined && <label htmlFor={id} className="form-check-label">{label}</label>}
    {children}
  </div>
);
Form.Text = ({ muted, className, children, ...props }: any) => (
  <small className={_cx('form-text', muted && 'text-muted', className)} {...props}>{children}</small>
);
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import {
  API_CALL_CENTRE_CREATE,
  API_COUNTRY_CODES_LIST,
  API_TIMEZONES_LIST,
  API_PARENT_CALL_CENTER_LICENCE,
  API_COUNTRY_CODE_DIRECTIONS_MASTER_LIST,
  API_USERS_LIST,
  API_DEMO_ACCOUNT_SETTINGS_LIST,
} from '@/service/url';
import CountryCodeDirectionPicker from '@/components/partials/dashboard/country-code-direction/CountryCodeDirectionPicker';
import LicenceAllocationPanel from '@/components/partials/dashboard/company/LicenceAllocationPanel';
import { postApiCall, getApiCall, fetchAndSetFlashMessage } from '@/service/httpCall';
import { useNavigate } from 'react-router-dom';
import {
  ROUTES,
  USER_ROLES,
  MODULE_ACL,
  FLASH_MESSAGE_ACTION_OBJECT,
} from '@/utilities/constant';
import { useAuth, errorCheck } from '@/views/dashboard/auth/auth-utils';
import { Storage, getUser, getUserCreateAccessRoleLabel } from '@/utilities/setting';
import {
  validateEmail,
  getUsernameValidationError,
  getNameValidationError,
  getUsernameTakenMessage,
  validateUserCoreFields,
  loadAclOptionsForRole,
  sendSignupEmailCode,
  verifySignupEmailOtp,
  generateRandomPassword,
} from '@/utilities/userCreation';
import { creatorRequiresEmailVerificationOnUserForms } from '@/utilities/userEmailVerificationAcl';
import { getIndiaDirectionIds } from '@/utilities/agentCountryCode';
import {
  filterDefaultCallCentreUserRoles,
  mustForceCallCentreAdminDefaultUser,
  resolveCallCentreDefaultUserRole,
} from '@/utilities/defaultCallCentreUserRoles';
import { useUsernameAvailability, useUserEmailAvailability } from '@/hooks/useAvailability';
import DefaultCallCentreUserForm from '@/components/partials/dashboard/call-centre/DefaultCallCentreUserForm';
import { parseAgentLeadsInput } from '@/utilities/agentLeadsQuota';
import {
  filterCallCentreCountryCodes,
  filterCallCentreTimezonesForCountry,
  filterCallCentreTimezonesList,
} from '@/utilities/callCentreFormFilters';
import {
  FiUser,
  FiGlobe,
  FiClock,
  FiAlignLeft,
  FiFlag,
  FiUsers,
  FiActivity,
  FiShare2,
  FiPhone,
  FiSmartphone,
  FiUserCheck,
  FiCpu,
} from 'react-icons/fi';
import { FaHeadset } from 'react-icons/fa';
import {
  hasCallCentreVoicemailAcl,
  getSelectableStorageQuotas,
  DEFAULT_STORAGE_QUOTA,
  getUserModuleOperations,
} from '@/utilities/callCentreVoicemailAcl';
import { hasCallCentreTranscriptionAcl } from '@/utilities/callCentreTranscriptionAcl';
import { hasCustomAgentLeads } from '@/utilities/agentLeadsQuota';
import { toDateInputString, minDateStr } from '@/utilities/expiryDate';
import { Card as ShadcnCard } from '@/components/ui/card';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { Button as ShadPageButton } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { UserPlus, Building2, Award, ArrowRight, Voicemail as VoicemailIcon, Mic as MicIcon } from 'lucide-react';
import { Stepper, useStepper, type StepDescriptor } from '@/components/ui/stepper';

const CREATE_CALL_CENTRE_STEPS: StepDescriptor[] = [
  { id: 'details', label: 'Details', description: 'Name, domain, timezone', icon: Building2 },
  { id: 'licence', label: 'Licence', description: 'Quotas from parent', icon: Award },
  { id: 'user', label: 'Default User', description: 'Optional admin', icon: UserPlus },
];
import { ArrowLeft } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import { ROUTES as PAGE_ROUTES } from '@/utilities/constant';
import '@/features/users/user-forms.css';

function IconField({ icon: Icon, children, hasTrailing, trailingIcon: TrailingIcon, textarea }) {
  return (
    <div
      className={`ccf-input-with-icon${textarea ? ' ccf-input-with-icon--textarea' : ''}${
        hasTrailing ? ' ccf-input-with-icon--trailing' : ''
      }${TrailingIcon ? ' ccf-input-with-icon--icon-right' : ''}`}
    >
      {!TrailingIcon && Icon && (
        <span className="ccf-input-with-icon__icon">
          <Icon size={18} />
        </span>
      )}
      {children}
      {TrailingIcon && (
        <span className="ccf-input-with-icon__icon ccf-input-with-icon__icon--right">
          <TrailingIcon size={18} />
        </span>
      )}
    </div>
  );
}

const CallCentreCreate = () => {
  const { checkAuth } = useAuth();
  const navigate = useNavigate();

  const stepper = useStepper(CREATE_CALL_CENTRE_STEPS.length);

  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [description, setDescription] = useState('');
  const [buttonDisable, setButtonDisable] = useState(false);
  const [countryCodeList, setCountryCodeList] = useState([]);
  const [countryCode, setCountryCode] = useState('');
  const [timezonesList, setTimezonesList] = useState([]);
  const [timezone, setTimezone] = useState('');
  const [filteredTimezones, setFilteredTimezones] = useState([]);
  const [parentLicence, setParentLicence] = useState(null);
  const [agent, setAgent] = useState(0);
  const [liveAgent, setLiveAgent] = useState(0);
  const [demoLiveAgents, setDemoLiveAgents] = useState(0);
  const [teamLead, setTeamLead] = useState(0);
  const [channel, setChannel] = useState(0);
  const [aiAgent, setAiAgent] = useState(0);
  const [aiAgentLiveChannel, setAiAgentLiveChannel] = useState(0);
  const [errors, setErrors] = useState({});
  const [loginUserRole, setLoginUserRole] = useState(null);
  const [nameError, setNameError] = useState('');
  const [domainError, setDomainError] = useState('');
  const [notes, setNotes] = useState([]);
  const [cps, setCps] = useState(0);
  const [mobile, setMobile] = useState(0);
  const [countryCodeDirections, setCountryCodeDirections] = useState([]);
  const [selectedCountryCodeDirections, setSelectedCountryCodeDirections] = useState([]);
  const indiaDirectionsDefaultedRef = useRef(false);

  const [defaultUserName, setDefaultUserName] = useState('');
  const [defaultUsername, setDefaultUsername] = useState('');
  const [defaultUsernameError, setDefaultUsernameError] = useState('');
  const [defaultNameError, setDefaultNameError] = useState('');
  const [defaultUsernameTakenMessage, setDefaultUsernameTakenMessage] = useState('');
  const [existingUsernames, setExistingUsernames] = useState([]);
  const [defaultUserEmail, setDefaultUserEmail] = useState('');
  const [initialPassword] = useState(generateRandomPassword);
  const [defaultUserPassword, setDefaultUserPassword] = useState(initialPassword);
  const [defaultUserConfirmPassword, setDefaultUserConfirmPassword] = useState(initialPassword);
  const [showDefaultPassword, setShowDefaultPassword] = useState(true);
  const [signupEmailOtp, setSignupEmailOtp] = useState('');
  const [signupEmailToken, setSignupEmailToken] = useState('');
  const [emailVerifiedForCreate, setEmailVerifiedForCreate] = useState(false);
  const [signupEmailBusy, setSignupEmailBusy] = useState(false);
  const [defaultUserRoleSelection, setDefaultUserRoleSelection] = useState('');
  const [defaultUserRoleOptions, setDefaultUserRoleOptions] = useState([]);
  const [defaultUserAclList, setDefaultUserAclList] = useState([]);
  const [defaultUserAcl, setDefaultUserAcl] = useState('');
  const [defaultUserExpiryDate, setDefaultUserExpiryDate] = useState('');
  const [defaultUserAgentLeads, setDefaultUserAgentLeads] = useState('100');
  const [parentExpiryDate, setParentExpiryDate] = useState(null);
  const [voicemailEnabled, setVoicemailEnabled] = useState(false);
  const [transcriptionEnabled, setTranscriptionEnabled] = useState(false);
  const [storageQuota, setStorageQuota] = useState(DEFAULT_STORAGE_QUOTA);
  const [createDefaultUser, setCreateDefaultUser] = useState(true);


  const defaultUsernameStatus = useUsernameAvailability(defaultUsername, {
    enabled: createDefaultUser && !getUsernameValidationError(defaultUsername),
  });
  const defaultEmailStatus = useUserEmailAvailability(defaultUserEmail, {
    enabled: createDefaultUser && !validateEmail(defaultUserEmail),
  });
  const [isDemoCreator, setIsDemoCreator] = useState(false);
  const [companyExpiry, setCompanyExpiry] = useState(null);

  const defaultUserMaxExpiry = minDateStr(parentExpiryDate, companyExpiry);
  const [demoSettings, setDemoSettings] = useState(null);

  useEffect(() => {
    getApiCall(API_DEMO_ACCOUNT_SETTINGS_LIST, {})
      .then((resp) => {
        if (Array.isArray(resp) && resp.length > 0) setDemoSettings(resp[0]);
      })
      .catch(() => {});
  }, []);

  const voicemailAclContext = useMemo(() => {
    try {
      const raw = Storage.getItem('user');
      if (!raw) return { userRole: null, isSuperAdmin: false };
      const user = JSON.parse(raw);
      return {
        userRole: user?.role,
        isSuperAdmin: user?.role === USER_ROLES.SUPERADMIN,
      };
    } catch {
      return { userRole: null, isSuperAdmin: false };
    }
  }, []);

  const defaultUserEmailVerificationRequired = useMemo(() => {
    try {
      const creatorUser = JSON.parse(Storage.getItem('user') || 'null');
      return creatorRequiresEmailVerificationOnUserForms(creatorUser, {
        isSuperAdmin: voicemailAclContext.isSuperAdmin,
        userRole: creatorUser?.role,
      });
    } catch {
      return false;
    }
  }, [voicemailAclContext.isSuperAdmin]);

  useEffect(() => {
    if (!defaultUserEmailVerificationRequired) {
      setSignupEmailToken('');
      setSignupEmailOtp('');
      setEmailVerifiedForCreate(false);
    }
  }, [defaultUserEmailVerificationRequired]);

  const userModuleOps = useMemo(() => {
    try {
      const raw = Storage.getItem('user');
      if (!raw) return {};
      return getUserModuleOperations(JSON.parse(raw));
    } catch {
      return {};
    }
  }, []);

  const canConfigureVoicemail = useMemo(
    () => hasCallCentreVoicemailAcl(userModuleOps, voicemailAclContext),
    [userModuleOps, voicemailAclContext],
  );

  const storageQuotaOptions = useMemo(
    () => getSelectableStorageQuotas(userModuleOps, voicemailAclContext),
    [userModuleOps, voicemailAclContext],
  );

  const canConfigureTranscription = useMemo(
    () => hasCallCentreTranscriptionAcl(userModuleOps, voicemailAclContext),
    [userModuleOps, voicemailAclContext],
  );

  const canConfigureLeads = useMemo(
    () => hasCustomAgentLeads(userModuleOps, voicemailAclContext),
    [userModuleOps, voicemailAclContext],
  );

  useEffect(() => {
    const user = getUser();
    const creatorRole = user?.role || null;
    if (creatorRole) setLoginUserRole(creatorRole);
    if (user?.expiry_date) setParentExpiryDate(user.expiry_date);

    const assigned = user?.assignedCompany || user?.assigned_company_details || null;
    if (assigned?.is_demo_company === true) {
      setIsDemoCreator(true);
      setCompanyExpiry(assigned?.expiry_date || user?.expiry_date || null);
    } else if (assigned?.expiry_date) {
      setCompanyExpiry(assigned.expiry_date);
    }

    getUserCreateAccessRoleLabel()
      .then((resp) => {
        const filtered = filterDefaultCallCentreUserRoles(resp || [], creatorRole);
        if (filtered.length) {
          setDefaultUserRoleOptions(filtered);
          setDefaultUserRoleSelection(
            resolveCallCentreDefaultUserRole(creatorRole, filtered[0].value),
          );
        } else if (mustForceCallCentreAdminDefaultUser(creatorRole)) {
          const forced = filterDefaultCallCentreUserRoles([], creatorRole);
          setDefaultUserRoleOptions(forced);
          setDefaultUserRoleSelection(USER_ROLES.CALL_CENTRE_ADMIN);
        } else {
          setDefaultUserRoleOptions([]);
          setDefaultUserRoleSelection('');
        }
      })
      .catch((err) => console.log(err));
  }, []);

  useEffect(() => {
    if (!mustForceCallCentreAdminDefaultUser(loginUserRole)) return;
    setDefaultUserRoleOptions(filterDefaultCallCentreUserRoles([], loginUserRole));
    setDefaultUserRoleSelection(USER_ROLES.CALL_CENTRE_ADMIN);
  }, [loginUserRole]);

  useEffect(() => {
    getApiCall(`${API_USERS_LIST}?role=noRole`, {})
      .then((data) => {
        setExistingUsernames((data || []).map((user) => user.username));
      })
      .catch((error) => console.error('Failed to load usernames', error));
  }, []);

  useEffect(() => {
    if (!defaultUserRoleSelection) {
      setDefaultUserAclList([]);
      setDefaultUserAcl('');
      return;
    }
    loadAclOptionsForRole(defaultUserRoleSelection)
      .then(({ list, defaultAclId }) => {
        setDefaultUserAclList(list);
        setDefaultUserAcl(defaultAclId);
      })
      .catch((err) => console.error(err));
  }, [defaultUserRoleSelection]);

  useEffect(() => {
    if (!countryCode || !timezonesList.length || !countryCodeList.length) {
      setFilteredTimezones([]);
      return;
    }
    const selectedCountry = countryCodeList.find((c) => c.id === countryCode);
    setFilteredTimezones(filterCallCentreTimezonesForCountry(timezonesList, selectedCountry));
  }, [countryCode, timezonesList, countryCodeList]);

  useEffect(() => {
    getApiCall(API_PARENT_CALL_CENTER_LICENCE)
      .then((resp) => {
        setParentLicence(resp?.parentLicence);
      })
      .catch((error) => {
        toast.warning(friendlyApiError(error));
      });

    fetchAndSetFlashMessage(
      (msg) =>
        msg.module_name === MODULE_ACL.CALL_CENTRE &&
        msg.action === FLASH_MESSAGE_ACTION_OBJECT.ADD &&
        msg.type === 'Note',
      setNotes,
    );
  }, []);

  useEffect(() => {
    if (!isDemoCreator) return;
    const exp = toDateInputString(companyExpiry || parentExpiryDate);
    if (!exp) return;
    setDefaultUserExpiryDate(exp);
  }, [isDemoCreator, companyExpiry, parentExpiryDate]);

  useEffect(() => {
    if (isDemoCreator || !defaultUserMaxExpiry) return;
    setDefaultUserExpiryDate((prev) => {
      if (!prev) return defaultUserMaxExpiry;
      return prev > defaultUserMaxExpiry ? defaultUserMaxExpiry : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDemoCreator, defaultUserMaxExpiry]);

  useEffect(() => {
    if (!isDemoCreator || !demoSettings) return;
    const perLive = Number(demoSettings.total_demo_live_agent_per_company) || 0;
    setAgent(String(Number(demoSettings.total_demo_agent_per_company) || 0));
    setTeamLead(String(Number(demoSettings.team_lead_per_company) || 0));
    setLiveAgent(String(perLive));
    setChannel(String(perLive * 2));
  }, [isDemoCreator, demoSettings]);

  const validateInput = (value, key) => {
    if (value === '' || value == null) return 0;
    const digitsOnly = String(value).replace(/\D/g, '');
    if (digitsOnly === '') return 0;
    const num = parseInt(digitsOnly, 10);
    let newErrors = { ...errors };

    if (Number.isNaN(num)) return 0;
    if (num < 0) {
      newErrors[key] = 'Value cannot be negative';
      setErrors(newErrors);
      return 0;
    }

    const licenceKeyMap = {
      agent: 'role_ccagent',
      live_agent: 'live_agent',
      team_lead: 'role_teamlead',
      channel: 'channel',
      cps: 'cps',
      mobile: 'mobile',
      reseller: 'reseller',
      company: 'company',
      demo_live_agents: 'demo_live_agents_per_reseller_company',
      ai_agent: 'ai_agent',
      ai_agent_live_channel: 'ai_agent_live_channel',
    };

    const licenceKey = licenceKeyMap[key];
    const availableValue = parentLicence?.available_licence?.[licenceKey];

    if (availableValue === 0 && num > 0) {
      newErrors[key] = `Value cannot be more than 0`;
      setErrors(newErrors);
      return num;
    }

    newErrors[key] = '';

    const nextAgent = key === 'agent' ? num : agent;
    const nextLiveAgent = key === 'live_agent' ? num : liveAgent;
    const nextTeamLead = key === 'team_lead' ? num : teamLead;

    if (nextAgent < nextLiveAgent) {
      newErrors.agent = 'Total Agent cannot be less than Live Agent';
      newErrors.live_agent = 'Live Agent cannot be more than Total Agent';
    } else {
      newErrors.agent = '';
      newErrors.live_agent = '';
    }

    if (nextTeamLead > nextAgent) {
      newErrors.team_lead = `Total Team Lead cannot be more than Total Agent (${nextAgent})`;
    } else {
      newErrors.team_lead = '';
    }

    if (availableValue != null && availableValue > 0 && num > availableValue) {
      newErrors[key] = `Value cannot be more than ${availableValue}`;
    }

    if (key === 'live_agent' && num > nextAgent) {
      newErrors.live_agent = `Live Agent cannot be more than Total Agent (${nextAgent})`;
    }
    if (key === 'team_lead' && num > nextAgent) {
      newErrors.team_lead = `Total Team Lead cannot be more than Total Agent (${nextAgent})`;
    }

    if (isDemoCreator) {
      const nextDemoLive = key === 'demo_live_agents' ? num : Number(demoLiveAgents) || 0;
      if (nextAgent > nextDemoLive) {
        newErrors.demo_live_agents = `Demo company: Total Agent cannot exceed Demo Live Agents (Reseller) (${nextDemoLive})`;
      } else if (
        String(newErrors.demo_live_agents || '').startsWith('Demo company: Total Agent')
      ) {
        newErrors.demo_live_agents = '';
      }
    }

    setErrors(newErrors);
    return num;
  };

  const getLicenceMax = (key) => {
    const avail = parentLicence?.available_licence;
    if (avail == null) return undefined;
    const map = {
      agent: avail.role_ccagent,
      live_agent: avail.live_agent,
      team_lead: avail.role_teamlead,
      channel: avail.channel,
      cps: avail.cps,
      mobile: avail.mobile,
      demo_live_agents: avail.demo_live_agents_per_reseller_company,
      ai_agent: avail.ai_agent,
      ai_agent_live_channel: avail.ai_agent_live_channel,
    };
    return map[key];
  };

  const stepLicence = (key, current, setter, delta) => {
    const next = Math.max(0, (parseInt(String(current), 10) || 0) + delta);
    setter(validateInput(String(next), key));
  };

  const canIncrementLicence = (key, current) => {
    const max = getLicenceMax(key);
    if (max === undefined || max === null) return true;
    return (parseInt(String(current), 10) || 0) < max;
  };

  useEffect(() => {
    getApiCall(API_COUNTRY_CODES_LIST, {}).then(onCountryCodeListSuccess).catch(onError);
    getApiCall(API_TIMEZONES_LIST, {}).then(onTimezonesListSuccess).catch(onError);
    getApiCall(API_COUNTRY_CODE_DIRECTIONS_MASTER_LIST, {})
      .then((data) => setCountryCodeDirections(Array.isArray(data) ? data : []))
      .catch(() => setCountryCodeDirections([]));
  }, []);

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (!countryCode || !timezonesList.length || !countryCodeList.length) {
      setFilteredTimezones([]);
      return;
    }
    const selectedCountry = countryCodeList.find((c) => c.id === countryCode);
    setFilteredTimezones(filterCallCentreTimezonesForCountry(timezonesList, selectedCountry));
  }, [countryCode, timezonesList, countryCodeList]);

  function onCountryCodeListSuccess(resp) {
    setCountryCodeList(filterCallCentreCountryCodes(resp));
  }

  function onTimezonesListSuccess(resp) {
    setTimezonesList(filterCallCentreTimezonesList(resp));
  }

  useEffect(() => {
    if (countryCode || !countryCodeList.length) return;
    const indiaCountry = countryCodeList.find((c) =>
      String(c?.name || '').toLowerCase().includes('india'),
    );
    if (indiaCountry) setCountryCode(indiaCountry.id);
  }, [countryCodeList, countryCode]);

  useEffect(() => {
    if (indiaDirectionsDefaultedRef.current) return;
    if (!countryCodeDirections.length) return;
    indiaDirectionsDefaultedRef.current = true;
    if (selectedCountryCodeDirections.length > 0) return;
    const ids = getIndiaDirectionIds(countryCodeDirections);
    if (ids.length) setSelectedCountryCodeDirections(ids);
  }, [countryCodeDirections, selectedCountryCodeDirections.length]);

  useEffect(() => {
    if (timezone || !countryCode || !filteredTimezones.length) return;
    const selectedCountry = countryCodeList.find((c) => c.id === countryCode);
    const isIndia = String(selectedCountry?.name || '').toLowerCase().includes('india');
    if (!isIndia) return;
    const defaultTimezone =
      filteredTimezones.find((tz) =>
        String(tz?.value || '').toLowerCase().includes('kolkata'),
      ) || filteredTimezones[0];
    if (defaultTimezone) setTimezone(defaultTimezone.id);
  }, [filteredTimezones, countryCode, countryCodeList, timezone]);

  function onError(err) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate);
    } else {
      console.log(err);
    }
  }

  const validateName = (value) => {
    const trimmedValue = value.trim();
    if (!trimmedValue) {
      setNameError('Name is required');
      return false;
    }
    if (trimmedValue.length < 3) {
      setNameError('Name must be at least 3 characters long');
      return false;
    }
    if (trimmedValue.length > 500) {
      setNameError('Name must not exceed 500 characters');
      return false;
    }
    const nameRegex = /^[a-zA-Z0-9_]+$/;
    if (!nameRegex.test(trimmedValue)) {
      setNameError(
        'Name can only contain letters, numbers, and underscores (_). No special characters allowed.',
      );
      return false;
    }
    setNameError('');
    return true;
  };

  const validateDomain = (value) => {
    const trimmedValue = value.trim();
    if (!trimmedValue) {
      setDomainError('Domain is required');
      return false;
    }
    if (trimmedValue.length > 500) {
      setDomainError('Domain must not exceed 500 characters');
      return false;
    }
    const urlRegex = /^https?:\/\/([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/.*)?$/;
    if (!urlRegex.test(trimmedValue)) {
      setDomainError(
        'Please enter a valid URL (e.g., https://example.com or http://example.com)',
      );
      return false;
    }
    setDomainError('');
    return true;
  };

  function validateForm() {
    let valid = true;
    let errorMessage = '';

    const nameValid = validateName(name);
    const domainValid = validateDomain(domain);

    if (!nameValid) {
      valid = false;
      errorMessage = nameError || 'Name validation failed';
    }
    if (!domainValid) {
      valid = false;
      errorMessage = errorMessage
        ? `${errorMessage}; ${domainError || 'Domain validation failed'}`
        : domainError || 'Domain validation failed';
    }
    if (!description.trim()) {
      valid = false;
      errorMessage = errorMessage
        ? `${errorMessage}; Description required`
        : 'Description required';
    }
    if (!countryCode.trim()) {
      valid = false;
      errorMessage = errorMessage
        ? `${errorMessage}; Country Code required`
        : 'Country Code required';
    }
    if (!timezone.trim()) {
      valid = false;
      errorMessage = errorMessage
        ? `${errorMessage}; Timezone required`
        : 'Timezone required';
    }

    if (!valid && errorMessage) {
      toast.warning(errorMessage);
    }

    return valid;
  }

  function validateDefaultUserFields() {
    let valid = true;

    const core = validateUserCoreFields({
      name: defaultUserName,
      username: defaultUsername,
      email: defaultUserEmail,
      password: defaultUserPassword,
      confirmPassword: defaultUserConfirmPassword,
      existingUsernames,
      role: defaultUserRoleSelection,
      aclId: defaultUserAcl,
      emailVerificationRequired: defaultUserEmailVerificationRequired,
      emailVerified: emailVerifiedForCreate,
      signupEmailToken,
      labelPrefix: 'Default user ',
      usernameStatus: defaultUsernameStatus,
      emailStatus: defaultEmailStatus,
    });

    if (!core.valid) valid = false;
    if (core.nameError) setDefaultNameError(core.nameError);
    if (core.usernameError) setDefaultUsernameError(core.usernameError);
    if (core.usernameTakenMessage) setDefaultUsernameTakenMessage(core.usernameTakenMessage);
    core.messages.forEach((message) => toast.error(message));

    if (canConfigureLeads) {
      const agentLeadsCheck = parseAgentLeadsInput(defaultUserAgentLeads);
      if (!agentLeadsCheck.ok) {
        valid = false;
        toast.error(agentLeadsCheck.message);
      }
    }

    if (!defaultUserExpiryDate) {
      valid = false;
      toast.error('Default user expiry date is required');
    } else if (defaultUserMaxExpiry && defaultUserExpiryDate > defaultUserMaxExpiry) {
      valid = false;
      toast.error(
        `Default user expiry cannot be later than ${defaultUserMaxExpiry} (your own / the company's expiry).`,
      );
    }
    const needsAgentLicence = defaultUserRoleSelection === USER_ROLES.CALL_CENTRE_AGENT;
    const needsTeamLeadLicence = defaultUserRoleSelection === USER_ROLES.CALL_CENTRE_TEAM_LEAD;
    if (needsAgentLicence && (parseInt(String(agent), 10) || 0) < 1) {
      valid = false;
      toast.error('Allocate at least 1 Total Agent licence for a default Agent user');
    }
    if (needsTeamLeadLicence && (parseInt(String(agent), 10) || 0) < 1) {
      valid = false;
      toast.error('Allocate at least 1 agent licence for a default Team Lead user');
    }
    return valid;
  }

  const handleDefaultUsernameChange = (e) => {
    const value = e.target.value;
    setDefaultUsername(value);
    const err = getUsernameValidationError(value);
    setDefaultUsernameError(err);
    const taken = getUsernameTakenMessage(value, existingUsernames);
    setDefaultUsernameTakenMessage(taken);
    if (err || taken) setButtonDisable(true);
    else setButtonDisable(false);
  };

  const handleDefaultUsernameBlur = (e) => {
    const err = getUsernameValidationError(e.target.value);
    setDefaultUsernameError(err);
    setDefaultUsernameTakenMessage(getUsernameTakenMessage(e.target.value, existingUsernames));
  };

  const handleDefaultNameChange = (e) => {
    const value = e.target.value;
    setDefaultUserName(value);
    setDefaultNameError(getNameValidationError(value));
  };

  const handleDefaultNameBlur = (e) => {
    setDefaultNameError(getNameValidationError(e.target.value));
  };

  const handleDefaultUserEmailChange = (e) => {
    setDefaultUserEmail(e.target.value);
    setSignupEmailToken('');
    setSignupEmailOtp('');
    setEmailVerifiedForCreate(false);
  };

  const sendDefaultUserSignupEmailCode = async () => {
    if (!defaultUserEmail.trim() || validateEmail(defaultUserEmail)) {
      toast.error('Enter a valid email before sending a code.');
      return;
    }
    setSignupEmailBusy(true);
    try {
      await sendSignupEmailCode(defaultUserEmail);
      setSignupEmailToken('');
      setEmailVerifiedForCreate(false);
      toast.success('Verification code sent. Check the inbox for this email.');
    } catch (err) {
      toast.error(friendlyApiError(err) || 'Could not send verification code');
    } finally {
      setSignupEmailBusy(false);
    }
  };

  const verifyDefaultUserSignupEmailOtp = async () => {
    if (!defaultUserEmail.trim() || validateEmail(defaultUserEmail)) {
      toast.error('Enter a valid email.');
      return;
    }
    if (!signupEmailOtp || signupEmailOtp.length !== 6) {
      toast.error('Enter the 6-digit code from the email.');
      return;
    }
    setSignupEmailBusy(true);
    try {
      const token = await verifySignupEmailOtp(defaultUserEmail, signupEmailOtp);
      if (token) {
        setSignupEmailToken(token);
        setEmailVerifiedForCreate(true);
        toast.success('Email verified.');
      } else {
        toast.error('Unexpected response from server');
      }
    } catch (err) {
      toast.error(friendlyApiError(err) || 'Verification failed');
      setEmailVerifiedForCreate(false);
      setSignupEmailToken('');
    } finally {
      setSignupEmailBusy(false);
    }
  };

  async function submitForm() {
    setButtonDisable(true);

    if (!validateForm()) {
      setButtonDisable(false);
      return;
    }
    if (createDefaultUser && !validateDefaultUserFields()) {
      setButtonDisable(false);
      return;
    }

    const params = {
      name,
      domain,
      description,
      countryCode,
      timezone,
      licence: {
        role_ccagent: agent,
        role_teamlead: teamLead,
        live_agent: liveAgent,
        team_lead: teamLead,
        channel: channel,
        cps: cps,
        mobile: mobile,
        reseller: 0,
        company: 0,
        demo_live_agents_per_reseller_company: demoLiveAgents,
        ai_agent: aiAgent,
        ai_agent_live_channel: aiAgentLiveChannel,
      },
    };

    if (selectedCountryCodeDirections?.length > 0) {
      params.country_code_directions = selectedCountryCodeDirections.map(Number);
    }

    if (canConfigureTranscription) {
      params.is_transcription_enabled = !!transcriptionEnabled;
    }

    if (canConfigureVoicemail) {
      params.is_voicemail_enabled = !!voicemailEnabled;
      if (voicemailEnabled) {
        params.storage_quota_in_mb = Number(storageQuota);
      }
    }

    if (createDefaultUser) {
      const agentLeadsParsed = parseAgentLeadsInput(defaultUserAgentLeads);
      params.default_user = {
        name: defaultUserName.trim(),
        username: defaultUsername.trim(),
        email: defaultUserEmail.trim(),
        password: defaultUserPassword,
        confirm_password: defaultUserConfirmPassword,
        ...(defaultUserEmailVerificationRequired && signupEmailToken
          ? { signup_email_token: signupEmailToken }
          : {}),
        expiry_date: defaultUserExpiryDate,
        ...(canConfigureLeads
          ? { agent_leads: agentLeadsParsed.ok ? agentLeadsParsed.value : 100 }
          : {}),
        role: resolveCallCentreDefaultUserRole(loginUserRole, defaultUserRoleSelection),
        acl_settings: defaultUserAcl,
      };
    }

    postApiCall(API_CALL_CENTRE_CREATE, params)
      .then(onCreateCreateSuccess)
      .catch(onCreateCreateError);
  }

  function onCreateCreateSuccess() {
    toast.success(
      createDefaultUser
        ? 'Call centre and default user created successfully'
        : 'Call centre created successfully',
    );
    setTimeout(() => {
      navigate(`/${ROUTES.CALL_CENTRES.LIST}`);
    }, 1500);
  }

  function onCreateCreateError(err) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate);
    } else {
      setButtonDisable(false);
      toast.warning(friendlyApiError(err));
    }
  }

  const handleCountryChange = (e) => {
    const selectedCountryId = e.target.value;
    setCountryCode(selectedCountryId);
    setTimezone('');
    const selectedCountry = countryCodeList.find((c) => c.id === selectedCountryId);
    setFilteredTimezones(filterCallCentreTimezonesForCountry(timezonesList, selectedCountry));
    setTimezone('');
  };

  const licenceNote =
    notes.find((n) => Number(n.priority) === 1)?.message ??
    'One Call Centers Licence is automatically used for the newly created Callcentre.';

  const licenceFooterNote =
    loginUserRole !== USER_ROLES.SUPERADMIN
      ? notes.find((n) => Number(n.priority) === 2)?.message ??
        'This licence detail is informational purpose and cannot be edited.'
      : '';

  const hasLicenceErrors = Object.values(errors).some((e) => e !== '');

  function validateStep0Details() {
    validateName(name);
    validateDomain(domain);
    if (!name || nameError) return false;
    if (!domain || domainError) return false;
    if (!countryCode) return false;
    if (!timezone) return false;
    if (!description) return false;
    return true;
  }
  function validateStep1Licence() {
    return !hasLicenceErrors;
  }
  function handleWizardNext() {
    if (stepper.current === 0 && !validateStep0Details()) return;
    if (stepper.current === 1 && !validateStep1Licence()) return;
    stepper.next();
  }

  return (
    <FillPage>
      <PageHeader
        title="New Call Center"
        description="Configure call center details and assign licences from your company pool."
        actions={
          <ShadPageButton asChild variant="outline" size="sm">
            <RouterLink to={`/${PAGE_ROUTES.CALL_CENTRES.LIST}`}>
              <ArrowLeft className="h-4 w-4" />
              Back
            </RouterLink>
          </ShadPageButton>
        }
      />
      <Row>
        <Col xl="12" lg="12">
          <ShadcnCard className="mt-4">
            <div className="call-centre-create-form-card p-4">
              <div className="new-user-info company-create-form call-centre-create-form">
                <div className="mb-4 flex items-center justify-between gap-3 border-b border-border/60 pb-3">
                  <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Call Center Details
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    Fields marked with <span className="text-destructive">*</span> are required
                  </div>
                </div>

                <form>
                  <Stepper
                    steps={CREATE_CALL_CENTRE_STEPS}
                    current={stepper.current}
                    completed={stepper.completed}
                    onStepClick={stepper.goTo}
                    className="mb-6"
                  />

                  {/* ── Step 0: Details ─────────────────────────────── */}
                  {stepper.current === 0 && (
                  <div className="row g-3 call-centre-create-form__row">
                    <Form.Group className="col-12 col-lg-6 form-group mb-0">
                      <Form.Label>
                        Name <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        id="name"
                        maxLength={500}
                        value={name}
                        className={nameError ? 'is-invalid' : ''}
                        placeholder="Call center name"
                        onChange={(e) => {
                          setName(e.target.value);
                          validateName(e.target.value);
                        }}
                        onBlur={(e) => validateName(e.target.value)}
                        required
                      />
                      {nameError && <div className="invalid-feedback d-block">{nameError}</div>}
                    </Form.Group>

                    <Form.Group className="col-12 col-lg-6 form-group mb-0">
                      <Form.Label>
                        Domain <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        id="domain"
                        maxLength={500}
                        value={domain}
                        className={domainError ? 'is-invalid' : ''}
                        placeholder="https://example.com"
                        onChange={(e) => {
                          setDomain(e.target.value);
                          validateDomain(e.target.value);
                        }}
                        onBlur={(e) => validateDomain(e.target.value)}
                        required
                      />
                      {domainError && (
                        <div className="invalid-feedback d-block">{domainError}</div>
                      )}
                    </Form.Group>

                    <Form.Group className="col-12 col-lg-6 form-group mb-0">
                      <Form.Label>
                        Country Code <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Select
                        id="countryCode"
                        required
                        value={countryCode}
                        onChange={handleCountryChange}
                      >
                        <option value="">Select country</option>
                        {countryCodeList.map((val) => (
                          <option key={val.id} value={val.id}>
                            {val.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    <Form.Group className="col-12 col-lg-6 form-group mb-0">
                      <Form.Label>
                        Timezone <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Select
                        id="timezone"
                        required
                        value={timezone}
                        disabled={!countryCode}
                        onChange={(e) => setTimezone(e.target.value)}
                      >
                        <option value="">Select timezone</option>
                        {filteredTimezones.map((val) => (
                          <option key={val.id} value={val.id}>
                            {val.value}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    <Form.Group className="col-12 form-group mb-0">
                      <Form.Label>
                        Description <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        id="description"
                        maxLength={500}
                        value={description}
                        placeholder="Describe this call center"
                        onChange={(e) => setDescription(e.target.value)}
                        required
                      />
                    </Form.Group>

                    <Form.Group className="col-12 col-lg-6 form-group mb-0">
                      <CountryCodeDirectionPicker
                        items={countryCodeDirections}
                        selectedIds={selectedCountryCodeDirections}
                        onChange={setSelectedCountryCodeDirections}
                        label="Country Codes"
                        showSelectAll
                        placeholder="Select country codes"
                      />
                    </Form.Group>

                    {canConfigureVoicemail ? (
                      <div className="col-12 col-lg-6 form-group mb-0">
                        <div className="flex items-center justify-between gap-3 rounded-lg border border-border/60 bg-muted/30 p-3">
                          <div className="flex items-center gap-3">
                            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-violet-100 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300">
                              <VoicemailIcon className="h-4 w-4" />
                            </span>
                            <div>
                              <div className="text-sm font-semibold text-foreground">
                                Voicemail
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {voicemailEnabled
                                  ? 'Enabled — agents can receive voicemails'
                                  : 'Disabled'}
                              </div>
                            </div>
                          </div>
                          <Switch
                            checked={!!voicemailEnabled}
                            onCheckedChange={(v) => setVoicemailEnabled(!!v)}
                            aria-label="Voicemail enabled"
                          />
                        </div>
                        {/* Storage quota (MB) — hidden for now.
                        {voicemailEnabled ? (
                          <div className="mt-2 flex items-center gap-2">
                            <label
                              htmlFor="ccVoicemailStorage"
                              className="whitespace-nowrap text-xs font-medium text-muted-foreground"
                            >
                              Storage quota (MB)
                            </label>
                            <Form.Select
                              id="ccVoicemailStorage"
                              value={storageQuota}
                              onChange={(e) => setStorageQuota(Number(e.target.value))}
                              className="max-w-[140px]"
                            >
                              {storageQuotaOptions.map((q) => (
                                <option key={q} value={q}>
                                  {q}
                                </option>
                              ))}
                            </Form.Select>
                          </div>
                        ) : null} */}
                      </div>
                    ) : null}

                    {canConfigureTranscription ? (
                      <div className="col-12 col-lg-6 form-group mb-0">
                        <div className="flex items-center justify-between gap-3 rounded-lg border border-border/60 bg-muted/30 p-3">
                          <div className="flex items-center gap-3">
                            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-violet-100 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300">
                              <MicIcon className="h-4 w-4" />
                            </span>
                            <div>
                              <div className="text-sm font-semibold text-foreground">
                                Transcription
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {transcriptionEnabled
                                  ? 'Enabled — agents can be given live-call transcription'
                                  : 'Disabled'}
                              </div>
                            </div>
                          </div>
                          <Switch
                            checked={!!transcriptionEnabled}
                            onCheckedChange={(v) => setTranscriptionEnabled(!!v)}
                            aria-label="Transcription enabled"
                          />
                        </div>
                      </div>
                    ) : null}
                  </div>
                  )}

                  {/* ── Step 1: Licence ─────────────────────────────── */}
                  {stepper.current === 1 && (
                  <LicenceAllocationPanel
                    showNote
                    noteMessage={licenceNote}
                    footerNote={licenceFooterNote}
                    columns={[
                      {
                        key: 'agent',
                        icon: FiUsers,
                        iconModifier: 'agent',
                        title: 'Total Agent',
                        description: 'Total number of agents',
                        value: parseInt(String(agent), 10) || 0,
                        onChange: (val) => setAgent(validateInput(String(val), 'agent')),
                        onDecrement: () => stepLicence('agent', agent, setAgent, -1),
                        onIncrement: () => stepLicence('agent', agent, setAgent, 1),
                        disabledMinus: (parseInt(String(agent), 10) || 0) <= 0,
                        disabledPlus: !canIncrementLicence('agent', parseInt(String(agent), 10) || 0),
                        error: errors.agent,
                        readOnly: isDemoCreator,
                      },
                      {
                        key: 'team_lead',
                        icon: FiUserCheck,
                        iconModifier: 'agent',
                        title: 'Total Team Lead',
                        description: 'Total number of team leads',
                        value: parseInt(String(teamLead), 10) || 0,
                        onChange: (val) => setTeamLead(validateInput(String(val), 'team_lead')),
                        onDecrement: () => stepLicence('team_lead', teamLead, setTeamLead, -1),
                        onIncrement: () => stepLicence('team_lead', teamLead, setTeamLead, 1),
                        disabledMinus: (parseInt(String(teamLead), 10) || 0) <= 0,
                        disabledPlus:
                          !canIncrementLicence('team_lead', parseInt(String(teamLead), 10) || 0) ||
                          (parseInt(String(teamLead), 10) || 0) >= (parseInt(String(agent), 10) || 0),
                        error: errors.team_lead,
                        readOnly: isDemoCreator,
                      },
                      {
                        key: 'live_agent',
                        icon: FiActivity,
                        iconModifier: 'live',
                        title: 'Live Agent',
                        description: 'Agents for live calls',
                        value: parseInt(String(liveAgent), 10) || 0,
                        onChange: (val) => setLiveAgent(validateInput(String(val), 'live_agent')),
                        onDecrement: () => stepLicence('live_agent', liveAgent, setLiveAgent, -1),
                        onIncrement: () => stepLicence('live_agent', liveAgent, setLiveAgent, 1),
                        disabledMinus: (parseInt(String(liveAgent), 10) || 0) <= 0,
                        disabledPlus: !canIncrementLicence(
                          'live_agent',
                          parseInt(String(liveAgent), 10) || 0,
                        ),
                        error: errors.live_agent,
                        readOnly: isDemoCreator,
                      },
                      {
                        key: 'channel',
                        icon: FiShare2,
                        iconModifier: 'channel',
                        title: 'Channels',
                        description: 'Communication channels',
                        value: parseInt(String(channel), 10) || 0,
                        onChange: (val) => setChannel(validateInput(String(val), 'channel')),
                        onDecrement: () => stepLicence('channel', channel, setChannel, -1),
                        onIncrement: () => stepLicence('channel', channel, setChannel, 1),
                        disabledMinus: (parseInt(String(channel), 10) || 0) <= 0,
                        disabledPlus: !canIncrementLicence('channel', parseInt(String(channel), 10) || 0),
                        error: errors.channel,
                        readOnly: isDemoCreator,
                      },
                      {
                        key: 'ai_agent',
                        icon: FiCpu,
                        iconModifier: 'ai',
                        title: 'AI Agents',
                        description: 'AI agents this call centre can create',
                        value: parseInt(String(aiAgent), 10) || 0,
                        onChange: (val) => setAiAgent(validateInput(String(val), 'ai_agent')),
                        onDecrement: () => stepLicence('ai_agent', aiAgent, setAiAgent, -1),
                        onIncrement: () => stepLicence('ai_agent', aiAgent, setAiAgent, 1),
                        disabledMinus: (parseInt(String(aiAgent), 10) || 0) <= 0,
                        disabledPlus: !canIncrementLicence(
                          'ai_agent',
                          parseInt(String(aiAgent), 10) || 0,
                        ),
                        error: errors.ai_agent,
                      },
                      {
                        key: 'ai_agent_live_channel',
                        icon: FiShare2,
                        iconModifier: 'ai',
                        title: 'AI Agent Live Channels',
                        description: 'Concurrent AI agent call channels',
                        value: parseInt(String(aiAgentLiveChannel), 10) || 0,
                        onChange: (val) =>
                          setAiAgentLiveChannel(validateInput(String(val), 'ai_agent_live_channel')),
                        onDecrement: () =>
                          stepLicence(
                            'ai_agent_live_channel',
                            aiAgentLiveChannel,
                            setAiAgentLiveChannel,
                            -1,
                          ),
                        onIncrement: () =>
                          stepLicence(
                            'ai_agent_live_channel',
                            aiAgentLiveChannel,
                            setAiAgentLiveChannel,
                            1,
                          ),
                        disabledMinus: (parseInt(String(aiAgentLiveChannel), 10) || 0) <= 0,
                        disabledPlus: !canIncrementLicence(
                          'ai_agent_live_channel',
                          parseInt(String(aiAgentLiveChannel), 10) || 0,
                        ),
                        error: errors.ai_agent_live_channel,
                      },
                      {
                        key: 'cps',
                        icon: FiPhone,
                        iconModifier: 'channel',
                        title: 'CPS',
                        description: 'Calls per second',
                        value: parseInt(String(cps), 10) || 0,
                        onChange: (val) => setCps(validateInput(String(val), 'cps')),
                        onDecrement: () => stepLicence('cps', cps, setCps, -1),
                        onIncrement: () => stepLicence('cps', cps, setCps, 1),
                        disabledMinus: (parseInt(String(cps), 10) || 0) <= 0,
                        disabledPlus: !canIncrementLicence('cps', parseInt(String(cps), 10) || 0),
                        error: errors.cps,
                      },
                      {
                        key: 'mobile',
                        icon: FiSmartphone,
                        iconModifier: 'mobile',
                        title: 'Mobile License',
                        description: 'Mobile Device licences',
                        value: parseInt(String(mobile), 10) || 0,
                        onChange: (val) => setMobile(validateInput(String(val), 'mobile')),
                        onDecrement: () => stepLicence('mobile', mobile, setMobile, -1),
                        onIncrement: () => stepLicence('mobile', mobile, setMobile, 1),
                        disabledMinus: (parseInt(String(mobile), 10) || 0) <= 0,
                        disabledPlus: !canIncrementLicence('mobile', parseInt(String(mobile), 10) || 0),
                        error: errors.mobile,
                      },
                      {
                        key: 'demo_live_agents',
                        icon: FiActivity,
                        iconModifier: 'live',
                        title: 'Demo Live Agents (Reseller)',
                        description: 'Demo live-agent pool for this reseller tree',
                        value: parseInt(String(demoLiveAgents), 10) || 0,
                        hidden: !(
                          Number(
                            parentLicence?.available_licence?.demo_live_agents_per_reseller_company || 0,
                          ) > 0
                        ),
                        onChange: (val) =>
                          setDemoLiveAgents(validateInput(String(val), 'demo_live_agents')),
                        onDecrement: () =>
                          stepLicence('demo_live_agents', demoLiveAgents, setDemoLiveAgents, -1),
                        onIncrement: () =>
                          stepLicence('demo_live_agents', demoLiveAgents, setDemoLiveAgents, 1),
                        disabledMinus: (parseInt(String(demoLiveAgents), 10) || 0) <= 0,
                        disabledPlus: !canIncrementLicence(
                          'demo_live_agents',
                          parseInt(String(demoLiveAgents), 10) || 0,
                        ),
                        error: errors.demo_live_agents,
                      },
                    ]}
                    availableSummary={
                      loginUserRole !== USER_ROLES.SUPERADMIN ? (
                        <div className="licence-available-summary">
                          <h6 className="licence-available-summary__title">
                            Available Licence (Allocated Company)
                          </h6>
                          <div className="licence-available-summary__row">
                            <span>
                              Call Centers: {parentLicence?.available_licence?.role_ccadmin ?? 0}
                            </span>
                            <span>Agent: {parentLicence?.available_licence?.role_ccagent ?? 0}</span>
                            <span>
                              Total Team Lead: {parentLicence?.available_licence?.role_teamlead ?? 0}
                            </span>
                            <span>Live Agent: {parentLicence?.available_licence?.live_agent ?? 0}</span>
                            <span>Channels: {parentLicence?.available_licence?.channel ?? 0}</span>
                            <span>AI Agents: {parentLicence?.available_licence?.ai_agent ?? 0}</span>
                            <span>
                              AI Live Channels:{' '}
                              {parentLicence?.available_licence?.ai_agent_live_channel ?? 0}
                            </span>
                            <span>CPS: {parentLicence?.available_licence?.cps ?? 0}</span>
                            <span>Mobile: {parentLicence?.available_licence?.mobile ?? 0}</span>
                          </div>
                        </div>
                      ) : null
                    }
                  />
                  )}

                  {/* ── Step 2: Default User ─────────────────────────── */}
                  {stepper.current === 2 && (
                  <>
                  <div className="flex items-center justify-between gap-3 rounded-lg border border-border/60 bg-muted/30 p-3">
                    <div className="flex items-center gap-3">
                      <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary">
                        <UserPlus className="h-4 w-4" />
                      </span>
                      <div>
                        <div className="text-sm font-semibold text-foreground">
                          Create Default User
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Optional primary login for this call center
                        </div>
                      </div>
                    </div>
                    <Switch
                      checked={!!createDefaultUser}
                      onCheckedChange={(v) => setCreateDefaultUser(!!v)}
                      aria-label="Create default user"
                    />
                  </div>

                  {createDefaultUser && canConfigureLeads && (
                    <div className="rounded-lg border border-border bg-card p-4">
                      <label htmlFor="default-user-agent-leads" className="mb-1 block text-sm font-semibold">
                        Agent leads <span className="text-destructive">*</span>
                      </label>
                      <input
                        id="default-user-agent-leads"
                        type="number"
                        min={0}
                        step={1}
                        value={defaultUserAgentLeads}
                        onChange={(e) => setDefaultUserAgentLeads(e.target.value)}
                        className="h-10 w-full max-w-xs rounded-md border border-input bg-background px-3 text-sm"
                      />
                      <p className="mt-1 text-xs text-muted-foreground">
                        Given to this call centre user. Default is 100. Saved on the user, not the call centre.
                      </p>
                    </div>
                  )}

                  {createDefaultUser && (
                    <DefaultCallCentreUserForm
                      name={defaultUserName}
                      setName={setDefaultUserName}
                      username={defaultUsername}
                      setUsername={setDefaultUsername}
                      usernameError={defaultUsernameError}
                      nameError={defaultNameError}
                      usernameTakenMessage={defaultUsernameTakenMessage}
                      usernameStatus={defaultUsernameStatus}
                      emailStatus={defaultEmailStatus}
                      onUsernameChange={handleDefaultUsernameChange}
                      onUsernameBlur={handleDefaultUsernameBlur}
                      onNameChange={handleDefaultNameChange}
                      onNameBlur={handleDefaultNameBlur}
                      email={defaultUserEmail}
                      onEmailChange={handleDefaultUserEmailChange}
                      password={defaultUserPassword}
                      setPassword={setDefaultUserPassword}
                      confirmPassword={defaultUserConfirmPassword}
                      setConfirmPassword={setDefaultUserConfirmPassword}
                      showPassword={showDefaultPassword}
                      setShowPassword={setShowDefaultPassword}
                      signupEmailOtp={signupEmailOtp}
                      setSignupEmailOtp={setSignupEmailOtp}
                      emailVerified={emailVerifiedForCreate}
                      signupEmailBusy={signupEmailBusy}
                      onSendCode={sendDefaultUserSignupEmailCode}
                      onVerifyOtp={verifyDefaultUserSignupEmailOtp}
                      emailVerificationRequired={defaultUserEmailVerificationRequired}
                      roleValue={defaultUserRoleSelection}
                      setRoleValue={setDefaultUserRoleSelection}
                      roleOptions={defaultUserRoleOptions}
                      hideRoleSelect={mustForceCallCentreAdminDefaultUser(loginUserRole)}
                      aclValue={defaultUserAcl}
                      setAclValue={setDefaultUserAcl}
                      aclList={defaultUserAclList}
                      expiryDate={defaultUserExpiryDate}
                      setExpiryDate={setDefaultUserExpiryDate}
                      agentLeads={defaultUserAgentLeads}
                      setAgentLeads={setDefaultUserAgentLeads}
                      showAgentLeads={canConfigureLeads}
                      maxExpiryDate={defaultUserMaxExpiry || parentExpiryDate}
                      disableExpiry={isDemoCreator}
                    />
                  )}

                  <div className="mt-6 flex justify-end">
                    <Button
                      onClick={submitForm}
                      type="button"
                      disabled={
                        buttonDisable ||
                        hasLicenceErrors ||
                        !!nameError ||
                        !!domainError ||
                        (createDefaultUser &&
                          (!!defaultUsernameError ||
                            !!defaultNameError ||
                            !!defaultUsernameTakenMessage))
                      }
                      className="gap-2"
                    >
                      {buttonDisable ? (
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                          aria-hidden="true"
                        />
                      ) : (
                        <FaHeadset />
                      )}
                      {createDefaultUser
                        ? 'Create Call Center & Default User'
                        : 'Create Call Center'}
                    </Button>
                  </div>
                  </>
                  )}

                  {/* Wizard nav — Back on any step > 0, Next until the
                      final step (which owns its own Submit above). */}
                  <div className="mt-6 flex items-center justify-between border-t border-border/60 pt-4">
                    <ShadPageButton
                      type="button"
                      variant="ghost"
                      onClick={stepper.prev}
                      disabled={stepper.current === 0}
                    >
                      <ArrowLeft className="h-4 w-4" />
                      Back
                    </ShadPageButton>
                    {stepper.current < CREATE_CALL_CENTRE_STEPS.length - 1 && (
                      <ShadPageButton type="button" onClick={handleWizardNext}>
                        Next
                        <ArrowRight className="h-4 w-4" />
                      </ShadPageButton>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </ShadcnCard>
        </Col>
      </Row>
    </FillPage>
  );
};

export default CallCentreCreate;
